import type { JobScopeItem, ScopeCategory } from "../../types";
import { makeId } from "../id";

const prompts: Record<ScopeCategory, string> = {
  SIZE: "What is the size or quantity of the job?",
  ACTIVITY: "What exact work needs to be done?",
  LOCATION: "Where is the work and what access constraints exist?",
  TIME: "When does the work need to happen?",
  EQUIPMENT: "What tools, parts, or materials are already available?",
};

function sentenceContaining(input: string, keywords: string[]): string | undefined {
  const sentences = input
    .split(/[.!?\n]/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

  return sentences.find((sentence) =>
    keywords.some((keyword) => sentence.toLowerCase().includes(keyword)),
  );
}

export function buildRuleBasedScope(input: string): JobScopeItem[] {
  const fallback = input.trim() || "Customer has not provided enough detail.";
  const candidates: Record<ScopeCategory, string | undefined> = {
    SIZE: sentenceContaining(fallback, ["one", "two", "feet", "small", "large", "single", "room"]),
    ACTIVITY: sentenceContaining(fallback, [
      "fix",
      "install",
      "move",
      "repair",
      "replace",
      "assemble",
      "clear",
    ]),
    LOCATION: sentenceContaining(fallback, [
      "basement",
      "garage",
      "kitchen",
      "bathroom",
      "yard",
      "outside",
      "attic",
    ]),
    TIME: sentenceContaining(fallback, [
      "today",
      "tomorrow",
      "week",
      "urgent",
      "emergency",
      "morning",
      "evening",
    ]),
    EQUIPMENT: sentenceContaining(fallback, [
      "tool",
      "parts",
      "material",
      "ladder",
      "paint",
      "hose",
      "hardware",
    ]),
  };

  return (Object.keys(prompts) as ScopeCategory[]).map((category) => ({
    id: makeId("scope"),
    category,
    prompt: prompts[category],
    description: candidates[category] ?? fallback.slice(0, 180),
    isVerified: Boolean(candidates[category]),
  }));
}
